import { Page } from "@/components";
import {
  Button,
  Card,
  Container,
  Flex,
  Grid,
  Skeleton,
  Text,
} from "@suns/design-system";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useAsync } from "@/shared/hooks";
import { SunsApiError } from "@suns/api";
import { personnelByIdLoader } from "./loaders/personnel-loader";
import {
  ContactInfo,
  ContactInfoView,
} from "@/components/ContactInfo/ContactInfo";
import { apolloApi } from "@/shared/api";
import { EntityIntelListings } from "@/components/EntityIntelListings/EntityIntelListing";

export function PersonnelView() {
  const { personnelId } = useParams();
  const navigate = useNavigate();

  const {
    response: personnel,
    loading: personnelLoading,
    error: personnelError,
  } = useAsync(personnelByIdLoader, {
    id: Number(personnelId),
  });

  const handleUpsertContactInfo = async (contactInfo: ContactInfo) => {
    const upsertResponse = await apolloApi.upsertPersonnel(contactInfo);
    return upsertResponse.personnel;
  };

  if (personnelError) {
    throw new SunsApiError("Error loading personnel", {
      cause: personnelError,
    });
  }

  return (
    <Page
      title={
        personnel
          ? `${personnel.firstName}  ${personnel.lastName}`
          : "View Personnel"
      }
      loading={personnelLoading}
      loadingState={<PersonnelViewLoading />}
      breadcrumbPaths={[
        { label: "Teams", link: "/teams" },
        { label: "Personnel", link: "/teams/personnel" },
      ]}
      render={() => {
        if (!personnel) throw new Error("Unable to load personnel");

        return (
          <Container size="xl">
            <Flex direction="down" gap="md">
              <Card>
                <Flex direction="down" gap="md">
                  <Flex direction="down" gap="xs">
                    <Text heading size="2xl">
                      {personnel?.firstName + " " + personnel?.lastName}
                    </Text>
                    <Link
                      className="text-sm font-bold text-primary"
                      to={`/teams/${personnel.currentTeamId}`}
                    >
                      {personnel.currentTeam?.fullName ??
                        personnel.currentTeam?.name}
                    </Link>
                  </Flex>
                  <ContactInfoView
                    contactInfo={personnel}
                    onSubmit={handleUpsertContactInfo}
                  />
                </Flex>
              </Card>
              <Card>
                <Flex gap="sm" justify="between">
                  <Text size="xl" heading>
                    Intel
                  </Text>
                  <Button
                    onClick={() =>
                      navigate(
                        `/reports/intel/create/personnel/${personnel.id}`
                      )
                    }
                  >
                    Create new Intel
                  </Button>
                </Flex>
                <EntityIntelListings />
              </Card>
            </Flex>
          </Container>
        );
      }}
    />
  );
}

function PersonnelViewLoading() {
  return (
    <Container size="xl">
      <Flex direction="down" gap="md">
        <Card>
          <Flex direction="down" gap="md">
            <Flex direction="down" gap="sm">
              <Skeleton className="h-8 w-72" />
              <Skeleton className="h-4 w-48" />
            </Flex>
            <Flex gap="md">
              <Skeleton className="h-10 w-24" />
              <Skeleton className="h-10 w-24" />
              <Skeleton className="h-10 w-24" />
            </Flex>
            <Grid columns="3" gap="sm">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </Grid>
          </Flex>
        </Card>
      </Flex>
    </Container>
  );
}
